import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * FH: keeps the sticky bar's quantity selector and the main product form's selector in sync.
 *
 * Horizon's sticky bar has no form of its own — it adds to cart by "puppet clicking" the main
 * product form's button (see src/js/sticky-add-to-cart.js), so the quantity that actually
 * reaches the cart is whatever is in the *main* form's input. A selector rendered in the bar is
 * outside that form and carries no `form` attribute, so on its own it would be decorative: the
 * shopper could set 3 and still get 1.
 *
 * This mirrors values both ways off the bubbling quantity-selector:update event, so either
 * selector can be the one the shopper touches.
 */
class FhStickyQuantity extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Mirrors a quantity change to the selector on the other side.
   * @param {Event & { detail?: { quantity?: number, cartLine?: number } }} event
   */
  #handleQuantityUpdate = (event) => {
    // Cart line updates come from the cart drawer, not the product form.
    if (event.detail?.cartLine) return;

    const quantity = event.detail?.quantity;
    if (typeof quantity !== 'number' || !Number.isFinite(quantity)) return;

    const origin = event.target instanceof Element ? event.target : null;
    const cameFromStickyBar = Boolean(origin && this.contains(origin));
    const target = cameFromStickyBar ? this.#mainSelector() : this.#ownSelector();

    if (!target) return;

    // setValue only writes to the input — it doesn't dispatch, so mirroring can't loop.
    target.setValue?.(String(quantity));
    target.updateButtonStates?.();
  };

  /**
   * The selector inside the sticky bar.
   * @returns {(Element & { setValue?: (v: string) => void, updateButtonStates?: () => void }) | null}
   */
  #ownSelector() {
    return this.querySelector('quantity-selector-component');
  }

  /**
   * The product form's selector — any quantity selector in this section that isn't ours.
   * @returns {(Element & { setValue?: (v: string) => void, updateButtonStates?: () => void }) | undefined}
   */
  #mainSelector() {
    const scope = this.closest('.shopify-section') ?? document;

    return /** @type {any} */ (
      Array.from(scope.querySelectorAll('quantity-selector-component')).find(
        (element) => !element.closest('sticky-add-to-cart')
      )
    );
  }
}

if (!customElements.get('fh-sticky-quantity')) {
  customElements.define('fh-sticky-quantity', FhStickyQuantity);
}
